import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Bookmark, Crown, Bell, Settings, Building2, Shield, LogIn, Store, Receipt, Handshake, Key, Loader2, Camera, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { enablePush } from "../lib/push";
import FeedCard from "../components/FeedCard";
import SEO from "../components/SEO";
import ImageUploader, { resolveImageUrl } from "../components/ImageUploader";
import LinkedAccounts from "../components/LinkedAccounts";

export default function Profile() {
  const { user, loading, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);
  const [txs, setTxs] = useState([]);
  const [showPw, setShowPw] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailCurrentPw, setEmailCurrentPw] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [isCommercial, setIsCommercial] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);

  useEffect(() => {
    if (user) {
      api.get("/me/saved").then(({ data }) => setSaved(data.items)).catch(() => {});
      api.get("/me/transactions").then(({ data }) => setTxs(data.items)).catch(() => {});
      api.get("/commercial/me").then(({ data }) => setIsCommercial(!!data.partner)).catch(() => {});
    }
    if (user?.must_change_password) setShowPw(true);
  }, [user]);

  if (loading) return <div className="py-20 text-center text-muted-foreground">Carregando...</div>;

  if (!user) {
    return (
      <div className="py-16 text-center space-y-5">
        <SEO title="Minha conta" path="/perfil" noindex />
        <div className="w-16 h-16 mx-auto rounded-3xl bg-primary/10 flex items-center justify-center">
          <LogIn className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-xl">Entre na sua conta</h1>
          <p className="text-sm text-muted-foreground mt-1">Salve favoritos, siga empresas e use a IA.</p>
        </div>
        <div className="flex gap-3 justify-center">
          <button data-testid="profile-login" onClick={() => navigate("/login")} className="rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold">Entrar</button>
          <button data-testid="profile-register" onClick={() => navigate("/cadastro")} className="rounded-full border border-border px-6 py-3 font-semibold">Cadastrar</button>
        </div>
      </div>
    );
  }

  const saveAvatar = async (url) => {
    setSavingAvatar(true);
    try {
      await api.put("/me/avatar", { avatar_url: url });
      await refresh();
      toast.success("Foto de perfil atualizada!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar foto");
    } finally { setSavingAvatar(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 6) return toast.error("A nova senha precisa ter ao menos 6 caracteres.");
    setPwSaving(true);
    try {
      const requiresCurrent = user.has_password && !user.must_change_password;
      await api.post("/me/password", {
        current_password: requiresCurrent ? pwCurrent : undefined,
        new_password: pwNew,
      });
      toast.success(user.has_password ? "Senha alterada com sucesso!" : "Senha definida! Agora você também pode entrar com e-mail e senha.");
      setPwCurrent(""); setPwNew(""); setShowPw(false);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao trocar senha");
    } finally { setPwSaving(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    const target = newEmail.trim().toLowerCase();
    if (!target || !/.+@.+\..+/.test(target)) return toast.error("Informe um e-mail válido.");
    if (target === (user.email || "").toLowerCase()) return toast.error("Este já é o seu e-mail atual.");
    setEmailSaving(true);
    try {
      await api.put("/me/email", {
        new_email: target,
        current_password: user.has_password ? emailCurrentPw : undefined,
      });
      toast.success("E-mail atualizado! Enviamos uma confirmação para o novo endereço.");
      setNewEmail(""); setEmailCurrentPw(""); setShowEmail(false);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao alterar e-mail");
    } finally { setEmailSaving(false); }
  };

  const passwordLabel = user.has_password ? "Trocar senha" : "Definir senha de acesso";
  const menu = [
    { icon: Key, label: passwordLabel, testid: "profile-change-password", action: () => setShowPw((v) => !v) },
    { icon: Mail, label: "Alterar e-mail", testid: "profile-change-email", action: () => setShowEmail((v) => !v) },
    { icon: Bell, label: "Ativar notificações", testid: "profile-alerts", action: async () => {
      try { await enablePush(); toast.success("Notificações ativadas!"); }
      catch (e) { toast.error(e.message || "Não foi possível ativar"); }
    } },
    { icon: Building2, label: "Empresas seguidas", testid: "profile-following" },
    { icon: Crown, label: "Meu plano", action: () => navigate("/planos"), testid: "profile-plan" },
    { icon: Settings, label: "Configurações", testid: "profile-settings" },
  ];

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 rounded-full overflow-hidden bg-primary flex items-center justify-center text-primary-foreground font-heading font-bold text-2xl shrink-0" data-testid="profile-avatar-wrapper">
          {user.avatar_url ? (
            <img src={resolveImageUrl(user.avatar_url)} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            <span>{user.name?.[0]?.toUpperCase()}</span>
          )}
        </div>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-xl">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold mt-1 capitalize">
            <Crown className="w-3 h-3" /> Plano {user.plan}
          </span>
        </div>
      </div>

      {/* Avatar uploader */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="profile-avatar-editor">
        <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Camera className="w-4 h-4 text-primary" /> Foto de perfil</p>
        {savingAvatar
          ? <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Salvando…</div>
          : <ImageUploader value={user.avatar_url || ""} onChange={saveAvatar} testId="profile-avatar" label="Enviar foto" />}
      </div>

      {/* Change password form */}
      {showPw && (
        <form data-testid="password-change-form" onSubmit={changePassword} className="rounded-2xl border border-primary/30 bg-primary/5 p-4 space-y-3">
          <p className="text-sm font-semibold">🔑 {user.has_password ? "Trocar minha senha" : "Definir minha senha de acesso"}</p>
          {!user.has_password && (
            <p className="text-xs text-primary">Sua conta foi criada via login social. Defina uma senha para também entrar pelo e-mail.</p>
          )}
          {user.must_change_password && (
            <p className="text-xs text-primary">Você está usando uma senha temporária. Escolha uma nova senha agora.</p>
          )}
          {user.has_password && !user.must_change_password && (
            <input data-testid="pw-current" type="password" required value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)} placeholder="Senha atual"
              className="w-full h-11 px-3 rounded-2xl bg-card border border-border text-sm" />
          )}
          <input data-testid="pw-new" type="password" required minLength={6} value={pwNew}
            onChange={(e) => setPwNew(e.target.value)} placeholder="Nova senha (mín. 6)"
            className="w-full h-11 px-3 rounded-2xl bg-card border border-border text-sm" />
          <button data-testid="pw-submit" type="submit" disabled={pwSaving}
            className="w-full h-11 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {pwSaving && <Loader2 className="w-4 h-4 animate-spin" />} {user.has_password ? "Salvar nova senha" : "Definir senha"}
          </button>
        </form>
      )}

      {/* Change email form */}
      {showEmail && (
        <form data-testid="email-change-form" onSubmit={changeEmail} className="rounded-2xl border border-primary/30 bg-primary/5 p-4 space-y-3">
          <p className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Alterar meu e-mail</p>
          <p className="text-xs text-muted-foreground">Atual: <b>{user.email}</b></p>
          <input data-testid="email-new" type="email" required value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)} placeholder="Novo e-mail"
            className="w-full h-11 px-3 rounded-2xl bg-card border border-border text-sm" />
          {user.has_password && (
            <input data-testid="email-current-pw" type="password" required value={emailCurrentPw}
              onChange={(e) => setEmailCurrentPw(e.target.value)} placeholder="Sua senha atual (para confirmar)"
              className="w-full h-11 px-3 rounded-2xl bg-card border border-border text-sm" />
          )}
          {!user.has_password && (
            <p className="text-xs text-primary">Sua conta foi criada via login social — vamos alterar sem pedir senha.</p>
          )}
          <button data-testid="email-submit" type="submit" disabled={emailSaving}
            className="w-full h-11 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {emailSaving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar novo e-mail
          </button>
        </form>
      )}

      {user.role === "admin" && (
        <button data-testid="profile-admin" onClick={() => navigate("/admin")}
          className="w-full flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
          <Shield className="w-5 h-5 text-primary" /><span className="font-semibold flex-1 text-left">Painel Admin</span>
        </button>
      )}

      {isCommercial ? (
        <button data-testid="profile-commercial-dashboard" onClick={() => navigate("/guia-comercial")}
          className="w-full flex items-center gap-3 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-secondary p-4">
          <Handshake className="w-5 h-5 text-primary" />
          <div className="text-left flex-1">
            <span className="font-semibold block">Meu painel Guia Comercial</span>
            <span className="text-xs text-muted-foreground">Ver clientes, link exclusivo e comissões</span>
          </div>
        </button>
      ) : (
        <button data-testid="profile-commercial-cta" onClick={() => navigate("/trabalhe-conosco")}
          className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <Handshake className="w-5 h-5 text-primary" />
          <div className="text-left flex-1">
            <span className="font-semibold block">Torne-se um Guia Comercial</span>
            <span className="text-xs text-muted-foreground">Ganhe 25% vitalício por cada indicação</span>
          </div>
        </button>
      )}

      <button data-testid="profile-mybusiness" onClick={() => navigate("/minha-empresa")}
        className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
        <Store className="w-5 h-5 text-primary" /><span className="font-semibold flex-1 text-left">Minha empresa</span>
      </button>

      <button data-testid="profile-dashboard" onClick={() => navigate("/painel")}
        className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
        <Building2 className="w-5 h-5 text-primary" /><span className="font-semibold flex-1 text-left">Dashboard empresarial</span>
      </button>

      <div className="rounded-3xl border border-border bg-card divide-y divide-border overflow-hidden">
        {menu.map((m) => (
          <button key={m.label} data-testid={m.testid} onClick={m.action}
            className="w-full flex items-center gap-3 p-4 hover:bg-muted transition-colors">
            <m.icon className="w-5 h-5 text-muted-foreground" /><span className="flex-1 text-left text-sm font-medium">{m.label}</span>
          </button>
        ))}
      </div>

      <LinkedAccounts />

      {txs.length > 0 && (
        <div data-testid="profile-transactions">
          <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Receipt className="w-5 h-5 text-primary" />Pagamentos</h2>
          <div className="rounded-3xl border border-border bg-card divide-y divide-border overflow-hidden">
            {txs.map((t) => {
              const st = { approved: ["Aprovado", "text-success"], pending: ["Pendente", "text-primary"],
                rejected: ["Recusado", "text-alert"] }[t.status] || [t.status, "text-muted-foreground"];
              return (
                <div key={t.id} data-testid={`tx-${t.id}`} className="flex items-center gap-3 p-4">
                  <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Crown className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium capitalize">Plano {t.plan}</p>
                    <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString("pt-BR")}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">R$ {Number(t.amount).toFixed(2).replace(".", ",")}</p>
                    <p className={`text-xs font-medium ${st[1]}`}>{st[0]}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Bookmark className="w-5 h-5 text-primary" />Favoritos</h2>
        {saved.length === 0 ? (
          <p className="text-sm text-muted-foreground">Você ainda não salvou nada. Explore o feed!</p>
        ) : (
          <div className="grid gap-6">
            {saved.map((p, i) => <FeedCard key={p.id} post={p} index={i} onAskAI={(post) => navigate(`/ia?p=${encodeURIComponent(post.title)}`)} />)}
          </div>
        )}
      </div>

      <button data-testid="profile-logout" onClick={async () => { await logout(); navigate("/"); }}
        className="w-full flex items-center justify-center gap-2 rounded-full border border-border py-3 font-semibold text-alert">
        <LogOut className="w-4 h-4" /> Sair
      </button>
    </div>
  );
}
